import Banner from "@/components/Banner";
import type { JobApplicantCountEstimate } from "./queries";
import {
  ESTIMATE_LOADING_DESCRIPTION,
  EXECUSEARCH_COPY,
  FACILITY_WEBSITE_NA_DESCRIPTION,
  formatEstimateBannerDescription,
  formatSampleSizeCopy,
  INCOMPLETE_CRITERIA_CAPTION,
  INCOMPLETE_CRITERIA_TITLE,
  isFacilityWebsiteApplyVia,
  isSparseSample,
  LOW_CONFIDENCE_COPY,
  MATCH_TIER_LABELS,
  SELF_SERVE_IN_REVIEW_COACHING,
  shouldShowCoachingCopy,
  shouldShowExecuSearchCopy
} from "./copy";

export type JobApplicantCountEstimateBlockProps = {
  estimate?: JobApplicantCountEstimate | null;
  /** Client gate. When true, do not flash incomplete copy while a range is in flight. */
  criteriaComplete?: boolean;
  serviceLevel?: string | null;
  stage?: string | null;
  applyVia?: string | null;
};

function IncompleteCriteriaBanner() {
  return (
    <Banner
      variant="info"
      inline
      description={INCOMPLETE_CRITERIA_TITLE}
      subText={INCOMPLETE_CRITERIA_CAPTION}
      testId="job-applicant-count-estimate-incomplete"
    />
  );
}

function FacilityWebsiteUnavailableBanner() {
  return (
    <Banner
      variant="info"
      inline
      description={FACILITY_WEBSITE_NA_DESCRIPTION}
      testId="job-applicant-count-estimate-facility-website"
    />
  );
}

function EstimateLoadingBanner() {
  return (
    <Banner
      variant="info"
      inline
      description={ESTIMATE_LOADING_DESCRIPTION}
      testId="job-applicant-count-estimate-range"
    />
  );
}

function EstimateBanner({
  estimate,
  serviceLevel,
  stage
}: {
  estimate: JobApplicantCountEstimate;
  serviceLevel?: string | null;
  stage?: string | null;
}) {
  const sampleCopy = formatSampleSizeCopy(estimate.sampleSize);
  const matchTierLabel = MATCH_TIER_LABELS[estimate.matchTier];
  const sparseSample = isSparseSample(estimate);
  const coaching = shouldShowCoachingCopy(estimate, serviceLevel, stage);
  const execuSearchCopy = shouldShowExecuSearchCopy(serviceLevel);
  const showSparseSampleCopy = sparseSample && !coaching;
  const showExecuSearchInBanner = execuSearchCopy && !coaching;

  return (
    <Banner
      variant={showSparseSampleCopy ? "warn" : "info"}
      inline
      description={formatEstimateBannerDescription(estimate.low, estimate.high)}
      subText={
        <>
          <span data-testid="job-applicant-count-estimate-match-tier">{matchTierLabel} match</span>
          {" · "}
          <span data-testid="job-applicant-count-estimate-sample">{sampleCopy}</span>
          {showSparseSampleCopy ? (
            <>
              {" · "}
              <span data-testid="job-applicant-count-estimate-low-confidence">{LOW_CONFIDENCE_COPY}</span>
            </>
          ) : null}
          {showExecuSearchInBanner ? (
            <>
              {" · "}
              <span data-testid="job-applicant-count-estimate-execusearch">{EXECUSEARCH_COPY}</span>
            </>
          ) : null}
        </>
      }
      testId="job-applicant-count-estimate-range"
    />
  );
}

export default function JobApplicantCountEstimateBlock({
  estimate,
  criteriaComplete,
  serviceLevel,
  stage,
  applyVia
}: JobApplicantCountEstimateBlockProps) {
  if (isFacilityWebsiteApplyVia(applyVia)) {
    return (
      <div className="flex flex-col gap-6" data-testid="job-applicant-count-estimate">
        <FacilityWebsiteUnavailableBanner />
      </div>
    );
  }

  if (estimate && !estimate.criteriaComplete) {
    return (
      <div className="flex flex-col gap-6" data-testid="job-applicant-count-estimate">
        <IncompleteCriteriaBanner />
      </div>
    );
  }

  if (!estimate) {
    if (criteriaComplete) {
      return (
        <div className="flex flex-col gap-6" data-testid="job-applicant-count-estimate">
          <EstimateLoadingBanner />
        </div>
      );
    }

    return (
      <div className="flex flex-col gap-6" data-testid="job-applicant-count-estimate">
        <IncompleteCriteriaBanner />
      </div>
    );
  }

  const sparseSample = isSparseSample(estimate);
  const coaching = shouldShowCoachingCopy(estimate, serviceLevel, stage);

  return (
    <div className="flex flex-col gap-6" data-testid="job-applicant-count-estimate">
      <EstimateBanner estimate={estimate} serviceLevel={serviceLevel} stage={stage} />
      {coaching ? (
        <Banner
          variant={sparseSample ? "warn" : "info"}
          inline
          description={SELF_SERVE_IN_REVIEW_COACHING}
          subText={sparseSample ? LOW_CONFIDENCE_COPY : undefined}
          testId="job-applicant-count-estimate-coaching"
        />
      ) : null}
    </div>
  );
}
